'use client';

import { PriceData, PriceDataContainer, Product } from '@/models/Product';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { useEffect, useState, useRef } from 'react';
import { Endpoints } from '@/lib/Endpoints';
import { HTTP_REQUEST } from '@/lib/Constants';
import { fetcher } from '@/lib/Requester';
import Link from 'next/link';
import axios from 'axios';
import { GenericResponse } from '@/models/GenericResponse';
import { SlugResponse } from '@/models/Slug';
import { Spinner } from './ui/spinner';
import { XIcon } from 'lucide-react';
import { Button } from './ui/button';
import AllowedProductListSwiper from './AllowedProductListSwiper';
import { AllowedProductsData } from '@/models/DeliveryData';

export default function AllowedProductDialog({
  visible,
  onClose,
  allowedProductsData,
}: {
  visible: boolean;
  onClose: () => void;
  allowedProductsData: AllowedProductsData;
}) {
  const [products, setProducts] = useState<Product[]>([]);
  const [pricesData, setPricesData] = useState<PriceData | null>(null);
  const [dialogWidth, setDialogWidth] = useState<number>(0);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (dialogRef.current) {
      const updateWidth = () => {
        const width = dialogRef.current?.clientWidth;
        if (width) setDialogWidth(width - 48);
      };

      updateWidth();
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }
  }, [visible, dialogRef.current]);

  useEffect(() => {
    const fetchProducts = async () => {
      if (!visible) return;
      const slug = allowedProductsData.slug;
      try {
        const params = {
          slug,
        };

        const { data: result } = await axios.get<GenericResponse<SlugResponse>>(
          `${process.env.NEXT_PUBLIC_BASE_URL}${Endpoints.GET_SLUG}`,
          { params },
        );
        setProducts(result.data.category?.products || []);
      } catch (e) {
        return null;
      }
    };

    fetchProducts();
  }, [visible]);

  useEffect(() => {
    if (!visible) return;

    const getPriceData = async () => {
      try {
        const response = await fetcher<PriceDataContainer>({
          params: {},
          cacheKey: '',
          endpoint: Endpoints.PRICES,
          method: HTTP_REQUEST.GET,
          cacheEnabled: false,
        });
        setPricesData(response?.items);
      } catch (e) {
        setPricesData(null);
      }
    };

    getPriceData();
  }, [visible]);

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent className="rounded-[24px]" ref={dialogRef}>
        <DialogTitle className="hidden" />
        <div className="flex flex-col gap-4 justify-center items-center">
          <Button variant="ghost" className="absolute top-5 right-2" onClick={onClose}>
            <XIcon className="size-4" />
          </Button>
          <span className="text-[18px] font-semibold font-fraunces text-[#2E2E2C]">
            Üzgünüz! 🥀
          </span>
          <p className="text-[13px] font-medium text-[#212121] text-center">
            Seçili ürünün {allowedProductsData.dialogWarningText}
          </p>
          <span className="text-[14px] font-semibold font-fraunces text-[#AF0E1D] sm:mt-4">
            {allowedProductsData.productsTitle}
          </span>
          {products.length > 0 ? (
            <AllowedProductListSwiper
              products={products}
              priceData={pricesData}
              width={dialogWidth}
              swiperText={allowedProductsData.swiperText}
              swiperImage={allowedProductsData.swiperImage}
            />
          ) : (
            <div className="flex flex-col gap-2 justify-center w-full">
              <Spinner className="mx-auto size-8 text-[#AF0E1D]" />
              <span className="text-sm font-medium text-[#AF0E1D] text-center">Yükleniyor...</span>
            </div>
          )}
          <Link
            href={`/${allowedProductsData.slug}`}
            className="w-full bg-[#AF0E1D] text-white font-medium text-[14px] py-2 px-4 rounded-full text-center"
          >
            {allowedProductsData.productsLinkText}
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}
